/**
 * Store link (`bistri.fi/<slug>`) rules of the setup flow. Identical to the
 * API's `src/store-setup/store-slug.util.ts` (plan §6.3); both repos run the
 * same `__fixtures__/slug-vectors.json`.
 */

export const SLUG_MIN = 3;
export const SLUG_MAX = 32;

export const RESERVED_SLUGS: ReadonlySet<string> = new Set([
  'admin',
  'api',
  'app',
  'assets',
  'auth',
  'billing',
  'blog',
  'cdn',
  'collection',
  'dashboard',
  'forgot',
  'go',
  'health',
  'help',
  'login',
  'logout',
  'mail',
  'marketplacelink',
  'me',
  'menu',
  'new',
  'news',
  'order',
  'orders',
  'partner',
  'partners',
  'qr',
  'reservation',
  'reservations',
  'reset',
  'setup',
  'signup',
  'staff',
  'static',
  'support',
  'table',
  'verify',
  'www',
]);

const FLOW_SLUG = /^[a-z0-9]{3,32}$/;
const FALLBACK_PREFIX = 'tienda';

/** NFD, strip combining marks, lowercase, keep [a-z0-9], cut to 32. */
export function normalizeSlug(text: string): string {
  return (text ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')
    .slice(0, SLUG_MAX);
}

/** What the owner types in the link field, sanitised on every keystroke. */
export function sanitizeSlugInput(text: string): string {
  return normalizeSlug(text);
}

/** normalizeSlug(name); shorter than 3 → + normalizeSlug(city); still shorter → 'tienda' + it (cut to 32). */
export function slugBase(name: string, city: string | null): string {
  let base = normalizeSlug(name);
  if (base.length < SLUG_MIN)
    base = (base + normalizeSlug(city ?? '')).slice(0, SLUG_MAX);
  if (base.length < SLUG_MIN)
    base = (FALLBACK_PREFIX + base).slice(0, SLUG_MAX);
  return base;
}

export function isValidFlowSlug(slug: string): boolean {
  return FLOW_SLUG.test(slug) && !RESERVED_SLUGS.has(slug);
}

/** The base cut so that base + suffix stays within 32 characters (the suffix is kept). */
function withSuffix(base: string, suffix: string): string {
  return base.slice(0, Math.max(0, SLUG_MAX - suffix.length)) + suffix;
}

/** [base, base+city, base+'2' … base+'9']: the base part is cut so each stays ≤32 (suffix kept), invalid/reserved/duplicates dropped. */
export function slugCandidates(base: string, city: string | null): string[] {
  const suffixes = ['', normalizeSlug(city ?? '')];
  for (let n = 2; n <= 9; n++) suffixes.push(String(n));
  const candidates: string[] = [];
  for (const suffix of suffixes) {
    const candidate = withSuffix(base, suffix);
    if (isValidFlowSlug(candidate) && !candidates.includes(candidate))
      candidates.push(candidate);
  }
  return candidates;
}
